import { useState, useEffect, useCallback } from 'react';
import { useNotify } from 'react-admin';
import { channelConnectionApi, ZapyInstanceStatus } from '../services/api.service';
import { useChannelSocket } from '../websocket';

export interface UseZapyInstanceOptions {
  channelConnectionId: number;
  onStatusChange?: (status: ZapyInstanceStatus) => void;
  /** Enable polling for status updates. Default: false (uses WebSocket) */
  pollingEnabled?: boolean;
}

export interface UseZapyInstanceReturn {
  status: ZapyInstanceStatus | null;
  loading: boolean;
  actionLoading: string | null;
  qrExpiresAt: number | null;
  isSocketConnected: boolean;
  fetchStatus: () => Promise<void>;
  refreshQRCode: () => Promise<void>;
  restart: () => Promise<void>;
  logout: () => Promise<void>;
}

export function useZapyInstance({
  channelConnectionId,
  onStatusChange,
  pollingEnabled = false,
}: UseZapyInstanceOptions): UseZapyInstanceReturn {
  const notify = useNotify();
  const [status, setStatus] = useState<ZapyInstanceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [qrExpiresAt, setQrExpiresAt] = useState<number | null>(null);

  // Subscribe to WebSocket for real-time updates
  const { connectionStatus, isSubscribed } = useChannelSocket(channelConnectionId);

  // Update status from WebSocket events
  useEffect(() => {
    if (connectionStatus) {
      const newStatus: ZapyInstanceStatus = {
        status: connectionStatus.status || 'disconnected',
        qrCode: connectionStatus.qrCode,
        profileName: connectionStatus.profileName,
        ownerJid: connectionStatus.ownerJid,
      };
      setStatus(newStatus);
      onStatusChange?.(newStatus);

      if (connectionStatus.qrExpiresAt) {
        setQrExpiresAt(connectionStatus.qrExpiresAt);
      }
    }
  }, [connectionStatus, onStatusChange]);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await channelConnectionApi.getZapyStatus(channelConnectionId);
      // Ensure we have a valid status object with at least a status property
      const statusData = response.data?.status ? response.data : { status: 'disconnected' };
      setStatus(statusData);
      onStatusChange?.(statusData);
    } catch {
      notify('resources.channel.notifications.statusFetchFailed', { type: 'error' });
      setStatus({ status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [channelConnectionId, notify, onStatusChange]);

  const refreshQRCode = useCallback(async () => {
    try {
      setActionLoading('qr');
      const response = await channelConnectionApi.getZapyQRCode(channelConnectionId);
      setStatus((prev) => (prev ? { ...prev, qrCode: response.data.qrCode } : null));
      setQrExpiresAt(response.data.expiresAt);
    } catch {
      notify('resources.channel.notifications.qrFetchFailed', { type: 'error' });
    } finally {
      setActionLoading(null);
    }
  }, [channelConnectionId, notify]);

  const restart = useCallback(async () => {
    try {
      setActionLoading('restart');
      await channelConnectionApi.restartZapyInstance(channelConnectionId);
      notify('resources.channel.notifications.restarted', { type: 'success' });
      // Status will be updated via WebSocket, no need to poll
      if (pollingEnabled) {
        setTimeout(fetchStatus, 2000);
      }
    } catch {
      notify('resources.channel.notifications.restartFailed', { type: 'error' });
    } finally {
      setActionLoading(null);
    }
  }, [channelConnectionId, notify, fetchStatus, pollingEnabled]);

  const logout = useCallback(async () => {
    try {
      setActionLoading('logout');
      await channelConnectionApi.logoutZapyInstance(channelConnectionId);
      notify('resources.channel.notifications.disconnected', { type: 'success' });
      // Status will be updated via WebSocket, no need to poll
      if (pollingEnabled) {
        setTimeout(fetchStatus, 2000);
      }
    } catch {
      notify('resources.channel.notifications.logoutFailed', { type: 'error' });
    } finally {
      setActionLoading(null);
    }
  }, [channelConnectionId, notify, fetchStatus, pollingEnabled]);

  // Initial fetch (always needed to get initial state)
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Auto-refresh QR code before expiration (only when polling enabled)
  useEffect(() => {
    if (!pollingEnabled || !qrExpiresAt || status?.status !== 'pending_qr_code_scan') return;

    const refreshBefore = 5000;
    const timeUntilRefresh = qrExpiresAt - Date.now() - refreshBefore;

    if (timeUntilRefresh <= 0) {
      refreshQRCode();
      return;
    }

    const timeout = setTimeout(refreshQRCode, timeUntilRefresh);
    return () => clearTimeout(timeout);
  }, [pollingEnabled, qrExpiresAt, status?.status, refreshQRCode]);

  // Auto-poll status when pending QR scan (only when polling enabled and no WebSocket)
  useEffect(() => {
    if (!pollingEnabled || status?.status !== 'pending_qr_code_scan') return;

    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, [pollingEnabled, status?.status, fetchStatus]);

  return {
    status,
    loading,
    actionLoading,
    qrExpiresAt,
    isSocketConnected: isSubscribed,
    fetchStatus,
    refreshQRCode,
    restart,
    logout,
  };
}
